import React, { useEffect, useState } from "react";
import { apiGet } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./DailyCollectionPanel.css";

interface BillRow {
  id: number;
  time: string;
  patient: string;
  mobile: string;
  drugs: string;
  amount: number;
}

interface CollectionData {
  total: number;
  supplier_paid: number;
  date: string;
  bills: BillRow[];
}

export default function DailyCollectionPanel() {
  const { showToast } = useToast();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<CollectionData>({
    total: 0,
    supplier_paid: 0,
    date: "",
    bills: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiGet(`/api/pharmacy/collection?date=${date}`)
      .then((res: unknown) => {
        const r = res as CollectionData;
        setData(r);
      })
      .catch((err) => {
        showToast({ message: "Failed to load collection data.", type: "error" });
        setData({ total: 0, supplier_paid: 0, date: date, bills: [] });
      })
      .finally(() => setLoading(false));
  }, [date, showToast]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: '100%' }}>
      {/* Top Widget: Total Collection */}
      <div style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', border: '1px solid #86efac', padding: '20px', borderRadius: '16px', boxShadow: 'var(--mod-shadow-sm)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Daily Revenue
            </span>
            <span style={{ fontSize: '2.5rem', fontWeight: 800, color: '#166534', lineHeight: 1.1 }}>
              ₹{data.total.toLocaleString()}
            </span>
          </div>
          <input 
            type="date" 
            value={date} 
            onChange={(e) => setDate(e.target.value)} 
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #86efac', background: 'rgba(255,255,255,0.8)', color: '#166534', fontWeight: 600, outline: 'none' }}
          />
        </div>
      </div>

      {/* Responsive Card List (Replaces old table) */}
      <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxShadow: 'var(--mod-shadow-sm)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>Pharmacy Bills</h3>
          <span style={{ fontSize: '0.8rem', color: '#64748b', background: '#e2e8f0', padding: '2px 10px', borderRadius: '12px', fontWeight: 600 }}>{data.bills.length}</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>Loading transactions...</div>
          ) : data.bills.length === 0 ? (
            <div className="mod-empty-state" style={{ height: '100%', border: '1px dashed #cbd5e1' }}>
              No bills generated for this date.
            </div>
          ) : (
            data.bills.map((bill) => (
              <div key={bill.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 2px 4px rgba(15,23,42,0.02)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong style={{ fontSize: '1rem', color: '#0f172a' }}>{bill.patient}</strong>
                    <span style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '6px', fontWeight: 600 }}>{bill.time}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>📞 {bill.mobile}</div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={bill.drugs}>
                    💊 {bill.drugs}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#16a34a' }}>₹{bill.amount.toLocaleString()}</span>
                  <Button variant="outline" size="sm" onClick={() => window.open(`/print?type=pharmacy&bill=${bill.id}`, "_blank")}>
                    🖨️ Print
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
